export const PLAN_IDS = {
  BASIS: "basis",
  PREMIUM: "premium",
  ELITE: "elite",
};

export const FEATURE_KEYS = {
  HOME: "home",
  USERS: "users",
  SITES: "sites",
  MEMBERS: "members",
  CONTRACTS: "contracts",
  SYSTEM: "system",
  XML_DIRECT_DEBIT: "xmlDirectDebit",
  NEWS: "news",
  TRAINING: "training",
  TASKS: "tasks",
  TESTINGS: "testings",
  TOURNAMENTS: "tournaments",
  PUBLIC_REGISTRATION: "publicRegistration",
  MEMBER_PORTAL: "memberPortal",
  EVENTS: "events",
  SURVEY: "survey",
  SHOP: "shop",
  TRAINING_ATTENDANCE: "trainingAttendance",
  E_DOJANG: "eDojang",
};

export const PLAN_ORDER = [PLAN_IDS.BASIS, PLAN_IDS.PREMIUM, PLAN_IDS.ELITE];

const LEGACY_PLAN_ALIASES = {
  starter: PLAN_IDS.BASIS,
  business: PLAN_IDS.PREMIUM,
  enterprise: PLAN_IDS.ELITE,
};

const planLimitMap = {
  [PLAN_IDS.BASIS]: { allowedUsers: 1, allowedSites: 1, allowedMembers: 50 },
  [PLAN_IDS.PREMIUM]: { allowedUsers: 3, allowedSites: 3, allowedMembers: 200 },
  [PLAN_IDS.ELITE]: { allowedUsers: 5, allowedSites: 5, allowedMembers: 500 },
};

export const PLAN_FEATURES = {
  [PLAN_IDS.BASIS]: {
    [FEATURE_KEYS.HOME]: true,
    [FEATURE_KEYS.USERS]: true,
    [FEATURE_KEYS.SITES]: true,
    [FEATURE_KEYS.MEMBERS]: true,
    [FEATURE_KEYS.CONTRACTS]: true,
    [FEATURE_KEYS.SYSTEM]: true,
    [FEATURE_KEYS.XML_DIRECT_DEBIT]: true,
  },
  [PLAN_IDS.PREMIUM]: {
    [FEATURE_KEYS.HOME]: true,
    [FEATURE_KEYS.USERS]: true,
    [FEATURE_KEYS.SITES]: true,
    [FEATURE_KEYS.MEMBERS]: true,
    [FEATURE_KEYS.CONTRACTS]: true,
    [FEATURE_KEYS.SYSTEM]: true,
    [FEATURE_KEYS.XML_DIRECT_DEBIT]: true,
    [FEATURE_KEYS.NEWS]: true,
    [FEATURE_KEYS.TRAINING]: true,
    [FEATURE_KEYS.TASKS]: true,
    [FEATURE_KEYS.TESTINGS]: true,
    [FEATURE_KEYS.TOURNAMENTS]: true,
    [FEATURE_KEYS.PUBLIC_REGISTRATION]: true,
    [FEATURE_KEYS.MEMBER_PORTAL]: true,
  },
  [PLAN_IDS.ELITE]: {
    [FEATURE_KEYS.HOME]: true,
    [FEATURE_KEYS.USERS]: true,
    [FEATURE_KEYS.SITES]: true,
    [FEATURE_KEYS.MEMBERS]: true,
    [FEATURE_KEYS.CONTRACTS]: true,
    [FEATURE_KEYS.SYSTEM]: true,
    [FEATURE_KEYS.XML_DIRECT_DEBIT]: true,
    [FEATURE_KEYS.NEWS]: true,
    [FEATURE_KEYS.TRAINING]: true,
    [FEATURE_KEYS.TASKS]: true,
    [FEATURE_KEYS.TESTINGS]: true,
    [FEATURE_KEYS.TOURNAMENTS]: true,
    [FEATURE_KEYS.PUBLIC_REGISTRATION]: true,
    [FEATURE_KEYS.MEMBER_PORTAL]: true,
    [FEATURE_KEYS.EVENTS]: true,
    [FEATURE_KEYS.SURVEY]: true,
    [FEATURE_KEYS.SHOP]: true,
    [FEATURE_KEYS.TRAINING_ATTENDANCE]: true,
    [FEATURE_KEYS.E_DOJANG]: true,
  },
};

export const PLAN_OPTIONS = [
  {
    id: PLAN_IDS.BASIS,
    title: "Basis",
    badge: null,
    limits: "1 Benutzer / 1 Standort",
    members: "50 Mitglieder",
    price: "5,90 € / Monat",
    allowedUsers: planLimitMap[PLAN_IDS.BASIS].allowedUsers,
    allowedSites: planLimitMap[PLAN_IDS.BASIS].allowedSites,
    allowedMembers: planLimitMap[PLAN_IDS.BASIS].allowedMembers,
    includedFeatures: [
      "Home / Dashboard",
      "Benutzer",
      "Standorte",
      "Mitglieder",
      "Vertragsdaten",
      "XML Lastschrift erzeugen",
    ],
  },
  {
    id: PLAN_IDS.PREMIUM,
    title: "Premium",
    badge: "Am beliebtesten",
    limits: "3 Benutzer / 3 Standorte",
    members: "200 Mitglieder",
    price: "39,90 € / Monat",
    allowedUsers: planLimitMap[PLAN_IDS.PREMIUM].allowedUsers,
    allowedSites: planLimitMap[PLAN_IDS.PREMIUM].allowedSites,
    allowedMembers: planLimitMap[PLAN_IDS.PREMIUM].allowedMembers,
    includedFeatures: [
      "Alles aus Basis, plus:",
      "Neuigkeiten",
      "Training",
      "Aufgaben",
      "Prüfungen",
      "Turniere",
      "Öffentliche Anmeldung",
      "Mitgliederzugang",
    ],
  },
  {
    id: PLAN_IDS.ELITE,
    title: "Elite",
    badge: null,
    limits: "5 Benutzer / 5 Standorte",
    members: "500 Mitglieder",
    price: "69,90 € / Monat",
    allowedUsers: planLimitMap[PLAN_IDS.ELITE].allowedUsers,
    allowedSites: planLimitMap[PLAN_IDS.ELITE].allowedSites,
    allowedMembers: planLimitMap[PLAN_IDS.ELITE].allowedMembers,
    includedFeatures: [
      "Alles aus Premium, plus:",
      "Veranstaltungen",
      "Befragungen",
      "Shop",
      "Anwesenheitsliste",
      "E-Dojang",
    ],
  },
];

const safePlanId = (planId) => {
  const normalized = String(planId || "")
    .trim()
    .toLowerCase();
  if (PLAN_ORDER.includes(normalized)) return normalized;
  if (LEGACY_PLAN_ALIASES[normalized]) return LEGACY_PLAN_ALIASES[normalized];
  return PLAN_IDS.BASIS;
};

export const normalizePlanId = safePlanId;

export const getPlanLimits = (planId) => planLimitMap[normalizePlanId(planId)];

export const hasFeatureAccess = (planId, featureKey) => {
  if (!featureKey) return true;
  const normalizedPlanId = normalizePlanId(planId);
  return Boolean(PLAN_FEATURES[normalizedPlanId]?.[featureKey]);
};

export const getPlanIndex = (planId) =>
  PLAN_ORDER.indexOf(normalizePlanId(planId));

export const getRequiredPlanForFeature = (featureKey) =>
  PLAN_ORDER.find((planId) => Boolean(PLAN_FEATURES[planId]?.[featureKey])) ||
  PLAN_IDS.BASIS;

export const getPlanOptionById = (planId) =>
  PLAN_OPTIONS.find((plan) => plan.id === normalizePlanId(planId)) ||
  PLAN_OPTIONS[0];

export const getUpgradePlanForFeature = (currentPlanId, featureKey) => {
  if (!featureKey) return PLAN_IDS.PREMIUM;
  const requiredPlan = getRequiredPlanForFeature(featureKey);
  return getPlanIndex(currentPlanId) < getPlanIndex(requiredPlan)
    ? requiredPlan
    : normalizePlanId(currentPlanId);
};
